import React from 'react';
import { cn } from 'features/lib/utils';

interface TooltipProps {
	children: React.ReactNode;
	content: React.ReactNode;
	className?: string;
	placement?: 'top' | 'bottom';
}

const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
	({ children, content, className, ...props }, ref) => {
		const [isVisible, setIsVisible] = React.useState(false);

		const showTooltip = () => {
			setIsVisible(true);
		};

		const hideTooltip = () => {
			setIsVisible(false);
		};

		const sharedStyles = {
			opacity: isVisible ? 1 : 0,
			transformOrigin: 'center',

			transition: 'opacity 150ms ease-out, transform 150ms ease-out'
		};

		const styles = {
			bottom: {
				top: '100%',
				left: '50%',
				right: 'auto',
				transform: isVisible
					? 'translateX(-50%) translateY(0) scaleX(1)'
					: 'translateX(-50%) translateY(-8px) scaleX(0.95)',
				marginTop: `4px`,
				...sharedStyles
			},

			top: {
				bottom: '100%',
				left: '50%',
				right: 'auto',
				transform: isVisible
					? 'translateX(-50%) translateY(0) scaleX(1)'
					: 'translateX(-50%) translateY(8px) scaleX(0.95)',
				marginBottom: `4px`,
				...sharedStyles
			}
		};

		return (
			<div
				ref={ref}
				className="relative"
				onMouseEnter={showTooltip}
				onMouseLeave={hideTooltip}
				onFocus={showTooltip}
				onBlur={hideTooltip}
				{...props}
			>
				{children}
				<div
					className={cn(
						'absolute z-50 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-md shadow-md',
						'pointer-events-none whitespace-nowrap max-w-xs',
						className
					)}
					style={styles[props.placement || 'top']}
					role="tooltip"
				>
					{content}
				</div>
			</div>
		);
	}
);

Tooltip.displayName = 'Tooltip';

export { Tooltip };
